import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Compass, Play, Trash, Check, ArrowRight, ShieldCheck } from '@phosphor-icons/react';

const EMPTY_FORM = {
  name: '', query: '', industry: '', city: '', region: '', country: '',
  source_types: ['google_places'], search_mode: 'company', intent_topic: '',
  intent_keywords_text: '', intent_exclude_keywords_text: '', intent_min_score: 25,
};

const EMAIL_MARKETING_PRESET = {
  intent_topic: 'E-Mail-Marketing',
  intent_keywords_text: 'Newsletter anmelden, Newsletter abonnieren, Onlineshop, Online Shop, Kundenclub, Kundenkarte, Veranstaltungen, Angebote, Aktionen, Neuigkeiten, Blog, Mitgliedschaft',
  intent_exclude_keywords_text: 'E-Mail-Marketing Agentur, Email Marketing Agentur, Newsletter Agentur, Marketing Automation Agentur, E-Mail-Marketing Software, Newsletter Software, Klaviyo Agentur, Mailchimp Agentur',
  intent_min_score: 25,
};

export default function DiscoveryJobsPage() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showResults, setShowResults] = useState(null);
  const [results, setResults] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  const fetchJobs = async () => {
    try { const { data } = await api.get('/discovery/jobs'); setJobs(data.items); } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetchJobs(); }, []);

  const createJob = async () => {
    setCreating(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim() || `${form.intent_topic} – ${form.query}`,
        intent_keywords: form.intent_keywords_text.split(',').map(value => value.trim()).filter(Boolean),
        intent_exclude_keywords: form.intent_exclude_keywords_text.split(',').map(value => value.trim()).filter(Boolean),
      };
      delete payload.intent_keywords_text;
      delete payload.intent_exclude_keywords_text;
      const { data: createdJob } = await api.post('/discovery/jobs', payload);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      fetchJobs();
      if (form.search_mode === 'public_intent') {
        const { data: runResult } = await api.post(`/discovery/jobs/${createdJob.id}/run`);
        toast.success(runResult.message);
      } else {
        toast.success(t('workspace.discovery.created'));
      }
      fetchJobs();
    } catch (err) { toast.error(formatApiError(err)); } finally { setCreating(false); }
  };

  const applyEmailMarketingPreset = () => {
    setForm(previous => ({ ...previous, search_mode: 'public_intent', ...EMAIL_MARKETING_PRESET }));
  };

  const runJob = async (jobId) => {
    try {
      const { data } = await api.post(`/discovery/jobs/${jobId}/run`);
      toast.success(data.message);
      fetchJobs();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteJob = async (jobId) => {
    try { await api.delete(`/discovery/jobs/${jobId}`); toast.success(t('workspace.discovery.deleted')); fetchJobs(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const viewResults = async (jobId) => {
    try {
      const { data } = await api.get('/discovery/raw-results', { params: { job_id: jobId, limit: 100 } });
      setResults(data.items);
      setShowResults(jobId);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const importResults = async (resultIds) => {
    try {
      const { data } = await api.post('/discovery/raw-results/import', { result_ids: resultIds });
      toast.success(t('workspace.discovery.importSummary', { imported: data.imported, skipped: data.skipped }));
      viewResults(showResults);
      fetchJobs();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="discovery-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">{t('workspace.discovery.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('workspace.discovery.subtitle')}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-black text-white hover:bg-gray-800" data-testid="create-job-btn">
          <Plus size={16} className="mr-2" /> {t('workspace.discovery.new')}
        </Button>
      </div>

      <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b border-gray-200">
            {['job', 'query', 'location', 'results', 'imported', 'status', 'actions'].map((key) => <th key={key} className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">{t(`workspace.discovery.${key}`)}</th>)}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="7" className="py-12 text-center text-gray-400">{t('common.loading')}</td></tr> :
            jobs.length === 0 ? <tr><td colSpan="7" className="py-12 text-center text-gray-400">{t('workspace.discovery.empty')}</td></tr> :
            jobs.map(j => (
              <tr key={j.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm font-medium text-gray-900">{j.name}</td>
                <td className="py-3 px-4 text-sm text-gray-600">
                  <div>{j.query || j.intent_topic || j.industry || '-'}</div>
                  {j.search_mode === 'public_intent' && <div className="text-[11px] text-indigo-600 mt-0.5">{t('workspace.discovery.publicIntent')}</div>}
                </td>
                <td className="py-3 px-4 text-sm text-gray-600">{[j.city, j.region].filter(Boolean).join(', ') || '-'}</td>
                <td className="py-3 px-4 font-mono text-sm">{j.result_count || 0}</td>
                <td className="py-3 px-4 font-mono text-sm text-emerald-700">{j.imported_count || 0}</td>
                <td className="py-3 px-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${
                    j.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    j.status === 'running' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    'bg-gray-100 text-gray-600 border-gray-200'
                  }`}>{j.status}</span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex gap-2">
                    {j.status === 'pending' && <button onClick={() => runJob(j.id)} className="text-blue-600 hover:text-blue-800" title={t('workspace.discovery.run')} data-testid={`run-job-${j.id}`}><Play size={16} /></button>}
                    {j.status === 'completed' && j.result_count > 0 && <button onClick={() => viewResults(j.id)} className="text-gray-600 hover:text-gray-900" title={t('workspace.discovery.viewResults')} data-testid={`view-results-${j.id}`}><ArrowRight size={16} /></button>}
                    <button onClick={() => deleteJob(j.id)} className="text-gray-400 hover:text-red-600" title={t('workspace.discovery.delete')}><Trash size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Job Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{t('workspace.discovery.dialogTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-mono uppercase text-gray-500 mb-1 block">{t('workspace.discovery.searchMode')}</label>
              <Select value={form.search_mode} onValueChange={value => setForm(p => ({ ...p, search_mode: value }))}>
                <SelectTrigger data-testid="discovery-search-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">{t('workspace.discovery.companySearch')}</SelectItem>
                  <SelectItem value="public_intent">{t('workspace.discovery.intentSearch')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.search_mode === 'public_intent' && (
              <div className="rounded-md border border-indigo-200 bg-indigo-50 p-4 space-y-4" data-testid="public-intent-fields">
                <div className="flex gap-2 text-indigo-900">
                  <ShieldCheck size={20} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{t('workspace.discovery.buyerSearchTitle')}</p>
                    <p className="text-xs leading-5 mt-1">{t('workspace.discovery.buyerSearchHelp')}</p>
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={applyEmailMarketingPreset} className="w-full bg-white border-indigo-300 text-indigo-800 hover:bg-indigo-100" data-testid="email-marketing-preset">
                  {t('workspace.discovery.emailMarketingPreset')}
                </Button>
                <div>
                  <label className="text-xs font-mono uppercase text-indigo-700 mb-1 block">1. {t('workspace.discovery.intentTopic')}</label>
                  <Input value={form.intent_topic} onChange={e => setForm(p => ({ ...p, intent_topic: e.target.value }))} placeholder={t('workspace.discovery.intentTopicPlaceholder')} data-testid="intent-topic-input" />
                  <p className="text-[11px] text-indigo-700 mt-1">{t('workspace.discovery.intentTopicHelp')}</p>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase text-indigo-700 mb-1 block">2. {t('workspace.discovery.targetCustomer')}</label>
                  <Input value={form.query} onChange={e => setForm(p => ({ ...p, query: e.target.value }))} placeholder={t('workspace.discovery.targetCustomerPlaceholder')} data-testid="target-customer-input" />
                  <p className="text-[11px] text-indigo-700 mt-1">{t('workspace.discovery.targetCustomerHelp')}</p>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase text-indigo-700 mb-1 block">3. {t('workspace.discovery.intentKeywords')}</label>
                  <Input value={form.intent_keywords_text} onChange={e => setForm(p => ({ ...p, intent_keywords_text: e.target.value }))} placeholder={t('workspace.discovery.intentKeywordsPlaceholder')} data-testid="intent-keywords-input" />
                  <p className="text-[11px] text-indigo-700 mt-1">{t('workspace.discovery.intentKeywordsHelp')}</p>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase text-indigo-700 mb-1 block">4. {t('workspace.discovery.intentExcludeKeywords')}</label>
                  <Input value={form.intent_exclude_keywords_text} onChange={e => setForm(p => ({ ...p, intent_exclude_keywords_text: e.target.value }))} placeholder={t('workspace.discovery.intentExcludePlaceholder')} data-testid="intent-exclude-input" />
                  <p className="text-[11px] text-indigo-700 mt-1">{t('workspace.discovery.intentExcludeHelp')}</p>
                </div>
                <details className="text-xs text-indigo-800">
                  <summary className="cursor-pointer font-medium">{t('workspace.discovery.advancedSettings')}</summary>
                  <div className="mt-3">
                    <label className="text-xs font-mono uppercase text-indigo-700 mb-1 block">{t('workspace.discovery.intentMinScore')}</label>
                    <Input type="number" min="0" max="100" value={form.intent_min_score} onChange={e => setForm(p => ({ ...p, intent_min_score: Number(e.target.value) }))} data-testid="intent-min-score-input" />
                  </div>
                </details>
                <p className="text-[11px] leading-4 text-indigo-700 border-t border-indigo-200 pt-3">{t('workspace.discovery.intentPrivacy')}</p>
              </div>
            )}
            {form.search_mode === 'company' && <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">{t('workspace.discovery.jobName')}</label>
              <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder={t('workspace.discovery.jobPlaceholder')} data-testid="job-name-input" /></div>
            }
            {form.search_mode === 'company' && <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">{t('workspace.discovery.searchQuery')}</label>
                <Input value={form.query} onChange={e => setForm(p => ({...p, query: e.target.value}))} placeholder={t('workspace.discovery.searchPlaceholder')} /></div>
              <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">{t('workspace.discovery.industry')}</label>
                <Input value={form.industry} onChange={e => setForm(p => ({...p, industry: e.target.value}))} placeholder={t('workspace.discovery.industryPlaceholder')} /></div>
            </div>}
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">{t('workspace.discovery.city')}</label>
                <Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} placeholder="Vancouver" /></div>
              <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">{t('workspace.discovery.region')}</label>
                <Input value={form.region} onChange={e => setForm(p => ({...p, region: e.target.value}))} placeholder="BC" /></div>
              <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">{t('workspace.discovery.country')}</label>
                <Input value={form.country} onChange={e => setForm(p => ({...p, country: e.target.value}))} placeholder="Canada" /></div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>{t('common.cancel')}</Button>
              <Button onClick={createJob} className="bg-black text-white hover:bg-gray-800" data-testid="save-job-btn" disabled={creating || (form.search_mode === 'company' ? !form.name : (!form.intent_topic.trim() || !form.query.trim() || !form.intent_keywords_text.trim()))}>
                {creating ? t('workspace.discovery.searching') : form.search_mode === 'public_intent' ? t('workspace.discovery.startBuyerSearch') : t('workspace.discovery.createLater')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={!!showResults} onOpenChange={() => setShowResults(null)}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{t('workspace.discovery.resultsTitle')}</DialogTitle></DialogHeader>
          <div className="mt-4">
            <div className="flex justify-end mb-3">
              <Button
                size="sm"
                onClick={() => importResults(results.filter(r => r.import_status === 'pending').map(r => r.id))}
                className="bg-black text-white hover:bg-gray-800"
                data-testid="import-all-results-btn"
                disabled={results.filter(r => r.import_status === 'pending').length === 0}
              >
                <Check size={14} className="mr-1" /> {t('workspace.discovery.importAll', { count: results.filter(r => r.import_status === 'pending').length })}
              </Button>
            </div>
            <table className="w-full text-sm table-fixed">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                <th className="py-2 px-2 text-left text-xs font-medium text-gray-500 uppercase w-[18%]">{t('workspace.discovery.company')}</th>
                <th className="py-2 px-2 text-left text-xs font-medium text-gray-500 uppercase w-[22%]">{t('workspace.discovery.website')}</th>
                <th className="py-2 px-2 text-left text-xs font-medium text-gray-500 uppercase w-[12%]">{t('workspace.discovery.industry')}</th>
                <th className="py-2 px-2 text-left text-xs font-medium text-gray-500 uppercase w-[10%]">{t('workspace.discovery.intentScore')}</th>
                <th className="py-2 px-2 text-left text-xs font-medium text-gray-500 uppercase w-[14%]">{t('workspace.discovery.location')}</th>
                <th className="py-2 px-2 text-left text-xs font-medium text-gray-500 uppercase w-[12%]">{t('workspace.discovery.status')}</th>
                <th className="py-2 px-2 text-left text-xs font-medium text-gray-500 uppercase w-[12%]">{t('workspace.discovery.action')}</th>
              </tr></thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.id} className="border-b border-gray-100">
                    <td className="py-2 px-2 text-gray-900">
                      <div className="truncate">{r.parsed_company_name}</div>
                      {r.intent_evidence?.[0] && (
                        <a
                          href={r.intent_evidence[0].url}
                          target="_blank"
                          rel="noreferrer"
                          className="block text-[10px] text-indigo-600 hover:underline truncate mt-1"
                          title={r.intent_evidence[0].excerpt}
                        >
                          {t('workspace.discovery.intentScore')}: {r.intent_evidence[0].excerpt}
                        </a>
                      )}
                    </td>
                    <td className="py-2 px-2 font-mono text-gray-600 text-xs truncate">{r.parsed_website || '-'}</td>
                    <td className="py-2 px-2 text-gray-600 truncate">{r.parsed_industry || '-'}</td>
                    <td className="py-2 px-2">
                      {r.intent_score == null ? '-' : (
                        <div title={(r.intent_matched_keywords || []).join(', ')}>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono border ${r.intent_score >= 70 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : r.intent_score >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>{r.intent_score}/100</span>
                          <div className="text-[10px] text-gray-400 truncate mt-1">{(r.intent_matched_keywords || []).join(', ')}</div>
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-gray-600 truncate">{[r.parsed_city, r.parsed_region].filter(Boolean).join(', ')}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono border ${
                        r.import_status === 'imported' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        r.import_status === 'duplicate' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-gray-100 text-gray-600 border-gray-200'
                      }`}>{r.import_status}</span>
                    </td>
                    <td className="py-2 px-3">
                      {r.import_status === 'pending' && (
                        <button onClick={() => importResults([r.id])} className="text-blue-600 hover:text-blue-800 text-xs font-medium">{t('workspace.discovery.import')}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Plus({ size, className }) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 256 256" className={className}><path fill="currentColor" d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"/></svg>;
}
